import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { Button, ICON } from '@attendance/ui';

/* ===========================================================================
 * THE ONE SAVE ON THE SETTINGS SCREEN, and the notice for when it fails.
 *
 * Extracted from `SettingsPage.tsx` — which is over its line budget and may
 * only shrink — when the company record joined the page's draft. That is the
 * point of the bar and worth stating here: this screen has exactly one save
 * mechanism. It writes the catalogue categories AND the company record in one
 * batch, so nothing on the page carries a Save of its own and no card has to
 * explain which button owns it.
 *
 * NOT the form's submit cluster in the §4b sense, and deliberately not moved
 * into the column: this page is a tabbed form where an edit can be made on a tab
 * you are no longer looking at, so the count and the save have to stay in view
 * from every tab.
 * ======================================================================== */

export function SettingsSaveBar({
  pendingCount,
  payCount,
  obstacle,
  busy,
  onDiscard,
  onSave,
}: {
  /** Everything unsaved — catalogue fields and company fields alike. */
  pendingCount: number;
  /** How many of those change what somebody is paid. */
  payCount: number;
  /** Why the save cannot go out, in words, or null when it can. */
  obstacle: string | null;
  busy: boolean;
  onDiscard: () => void;
  onSave: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    /* `--shadow-sticky` is the role for a bar the content has scrolled under,
       which is exactly what this is, and it gives the bar an edge that the
       translucent fill alone does not.

       `max-w-content` rather than `max-w-5xl`: the bar is fixed and full-bleed,
       so its inner cluster has to line up with the content column above it, and
       1024px was a number that agreed with nothing. */
    <div className="fixed inset-x-0 bottom-0 z-20 border-t border-border bg-surface/95 p-3 shadow-sticky backdrop-blur supports-[backdrop-filter]:bg-surface/80">
      <div className="mx-auto flex max-w-content flex-wrap items-center justify-between gap-inline">
        <p className="text-copy text-foreground">
          {t('settings.pendingCount', { count: pendingCount })}
          {payCount > 0 && (
            <span className="ms-2 inline-flex items-center gap-tight text-warning-on-surface">
              <AlertTriangle size={ICON.inline} aria-hidden="true" />
              {t('settings.pendingPay', { count: payCount })}
            </span>
          )}
        </p>
        <div className="flex flex-wrap items-center gap-element">
          <Button
            type="button"
            variant="outline"
            size="touch"
            onClick={onDiscard}
            disabled={busy}
          >
            {t('settings.discard')}
          </Button>
          {/* A BLOCKED SAVE NAMES ITS OBSTACLE (§3.11). One bar now covers the
              company record too, so a field it cannot legally write has to say
              which field and why HERE — a greyed button whose reason is two
              cards up the page is the dead control the rule is written
              against. */}
          {obstacle && (
            <p role="status" className="text-copy-sm text-warning-on-surface">
              {obstacle}
            </p>
          )}
          <Button
            type="button"
            size="touch"
            onClick={onSave}
            disabled={busy || obstacle !== null}
            aria-busy={busy}
          >
            {busy ? t('settings.saving') : t('settings.saveChanges')}
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * The save did not go through, and the edits were put back to what the server
 * holds. A failed save must never look like a saved one.
 */
export function SettingsSaveFailure({
  message,
  onRestore,
  onClose,
}: {
  message: string;
  onRestore: () => void;
  onClose: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <div
      role="alert"
      /* `shadow-popover`: it floats over the page, and `popover` is the role for
         that — the system's one two-layer shadow, a wide soft pool plus a tight
         contact edge, because one layer cannot be both and a panel with only the
         pool looks pasted on.

         `max-w-form` rather than `max-w-3xl`: 720px is the reading-column ROLE,
         and this bar is three sentences and two buttons.

         z-50, not z-30. This is a FAILURE notice — it was tying with the page
         toolbar's own z-30, where the winner is whichever comes later in the
         DOM. A message telling somebody their work was not saved is the last
         thing that should be settled by document order. */
      className="fixed inset-x-0 bottom-20 z-50 mx-auto max-w-form rounded-panel border border-danger-border bg-danger-surface p-card shadow-popover"
    >
      <p className="text-copy font-medium text-danger-on-surface">
        {t('settings.saveFailed')}
      </p>
      <p className="mt-1 text-copy-sm text-foreground">{message}</p>
      <p className="mt-1 text-copy-sm text-foreground">
        {t('settings.saveFailedReverted')}
      </p>
      <div className="mt-3 flex flex-wrap gap-element">
        <Button type="button" size="touch" onClick={onRestore}>
          {t('settings.restoreEdits')}
        </Button>
        <Button type="button" variant="outline" size="touch" onClick={onClose}>
          {t('common.close')}
        </Button>
      </div>
    </div>
  );
}
